/**
 * Frontend Sentry initialization.
 *
 * Soft-fails — when `@sentry/react` isn't installed or `VITE_SENTRY_DSN` isn't
 * set, everything no-ops. `@sentry/react` is listed in package.json's
 * `optionalDependencies` so `npm ci` succeeds without it.
 *
 * Env vars (set in .env.local / CI):
 *   VITE_SENTRY_DSN                  enable
 *   VITE_SENTRY_ENVIRONMENT          dev|staging|production
 *   VITE_SENTRY_RELEASE              git SHA
 *   VITE_SENTRY_TRACES_SAMPLE_RATE   default 0.1
 */

let initialized = false

type SentryEvent = { tags?: Record<string, string> }
type SentryModule = {
  init: (opts: Record<string, unknown>) => void
  browserTracingIntegration: () => unknown
}

export async function initSentry(component = 'dashboard-ui'): Promise<boolean> {
  const env = (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env
  const dsn = env.VITE_SENTRY_DSN
  if (!dsn || initialized) return initialized

  try {
    // Direct import so Vite can statically resolve + bundle the module.
    // The variable-indirection form (`const pkg = '...'; import(pkg)`) fails
    // in the browser because bare specifiers aren't resolvable at runtime.
    // `@sentry/react` lives in optionalDependencies — `npm ci` installs it by
    // default, so this works; minimal installs (`--omit=optional`) will
    // break the build, which is acceptable since DSN is opt-in anyway.
    const Sentry = (await import('@sentry/react')) as SentryModule
    const rateRaw = Number(env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1')
    const tracesRate = Number.isFinite(rateRaw) ? Math.max(0, Math.min(1, rateRaw)) : 0.1

    Sentry.init({
      dsn,
      environment: env.VITE_SENTRY_ENVIRONMENT ?? 'dev',
      release: env.VITE_SENTRY_RELEASE ?? 'unknown',
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: tracesRate,
      beforeSend(event: SentryEvent) {
        event.tags = { ...(event.tags ?? {}), 'qyngent.component': component }
        return event
      },
    })
    initialized = true
    return true
  } catch (err) {
    console.warn('[sentry] init failed — skipping', err)
    return false
  }
}

export function sentryEnabled(): boolean {
  return initialized
}
